import { toNestErrors, validateFieldsNatively } from "@hookform/resolvers";
import {
  appendErrors,
  type FieldError,
  type FieldValues,
  type Resolver,
  type ResolverError,
  type ResolverSuccess,
} from "react-hook-form";
import * as z4 from "zod";

const isZod4Error = (error: any): error is z4.ZodError => {
  // instanceof is safe in Zod 4 (uses Symbol.hasInstance)
  return error instanceof z4.ZodError;
};
const isZod4Schema = (schema: any): schema is z4.ZodType => {
  return "_zod" in schema && typeof schema._zod === "object";
};

function parseZod4Issues(
  issues: z4.core.$ZodIssue[],
  validateAllFieldCriteria: boolean,
  translateMessage: (message: string) => string,
) {
  // Schema messages are English source text (`msg()` in shared); show them in the reader's language.
  const translateIssue = (issue: z4.core.$ZodIssue): z4.core.$ZodIssue => ({
    ...issue,
    message: translateMessage(issue.message),
  });
  const zodErrors = issues.map(translateIssue);
  const errors: Record<string, FieldError> = {};
  // const _zodErrors = zodErrors as z4.$ZodISsue; //
  for (; zodErrors.length; ) {
    const error = zodErrors[0];
    const { code, message, path } = error;
    const _path = path.join(".");

    if (!errors[_path]) {
      if (error.code === "invalid_union") {
        const unionError = translateIssue(error.errors[0][0]);

        errors[_path] = {
          message: unionError.message,
          type: unionError.code,
        };
      } else {
        errors[_path] = { message, type: code };
      }
    }

    if (error.code === "invalid_union") {
      for (const unionError of error.errors) {
        for (const e of unionError) {
          zodErrors.push(translateIssue(e));
        }
      }
    }

    if (validateAllFieldCriteria) {
      const types = errors[_path].types;
      const messages = types?.[error.code];

      errors[_path] = appendErrors(
        _path,
        validateAllFieldCriteria,
        errors,
        code,
        messages ? ([] as string[]).concat(messages as string[], error.message) : error.message,
      ) as FieldError;
    }

    zodErrors.shift();
  }

  return errors;
}

type RawResolverOptions = {
  mode?: "async" | "sync";
  raw: true;
  /** Translates each issue's message, such as the portal's `useTranslateText()`. */
  translateMessage?: (message: string) => string;
};
type NonRawResolverOptions = {
  mode?: "async" | "sync";
  raw?: false;
  /** Translates each issue's message, such as the portal's `useTranslateText()`. */
  translateMessage?: (message: string) => string;
};

type Zod4ParseParams = z4.core.ParseContext<z4.core.$ZodIssue>;

export function zodResolver<
  Input extends FieldValues,
  Context,
  Output,
  T extends z4.ZodType<Output, Input> = z4.ZodType<Output, Input>,
>(
  schema: T,
  schemaOptions?: Zod4ParseParams, // already partial
  resolverOptions?: NonRawResolverOptions,
): Resolver<z4.input<T>, Context, z4.output<T>>;
export function zodResolver<
  Input extends FieldValues,
  Context,
  Output,
  T extends z4.ZodType<Output, Input> = z4.ZodType<Output, Input>,
>(
  schema: z4.ZodType<Output, Input>,
  schemaOptions: Zod4ParseParams | undefined, // already partial
  resolverOptions: RawResolverOptions,
): Resolver<z4.input<T>, Context, z4.input<T>>;
/**
 * Creates a resolver function for react-hook-form that validates form data using a Zod schema
 * @param {z4.ZodType<Input>} schema - The Zod schema used to validate the form data
 * @param {Zod4ParseParams} [schemaOptions] - Optional configuration options for Zod parsing
 * @param {Object} [resolverOptions] - Optional resolver-specific configuration
 * @param {('async'|'sync')} [resolverOptions.mode='async'] - Validation mode. Use 'sync' for synchronous validation
 * @param {boolean} [resolverOptions.raw=false] - If true, returns the raw form values instead of the parsed data
 * @returns {Resolver<z4.output<typeof schema>>} A resolver function compatible with react-hook-form
 * @throws {Error} Throws if validation fails with a non-Zod error
 * @example
 * const schema = z4.object({
 *   name: z4.string().min(2),
 *   age: z4.number().min(18)
 * });
 *
 * useForm({
 *   resolver: zodResolver(schema)
 * });
 */
export function zodResolver<Input extends FieldValues, Context, Output>(
  schema: object,
  schemaOptions?: object,
  resolverOptions: {
    mode?: "async" | "sync";
    raw?: boolean;
    translateMessage?: (message: string) => string;
  } = {},
): Resolver<Input, Context, Output | Input> {
  if (isZod4Schema(schema)) {
    return async (values: Input, _, options) => {
      try {
        if (process.env.NODE_ENV !== "production") {
          // biome-ignore lint/suspicious/noConsole: temporary form debugging requested by user
          console.log("[Form/ZodResolver] validating", {
            names: options.names,
            fields: Object.keys(options.fields ?? {}),
            values,
          });
        }
        const parseFn = resolverOptions.mode === "sync" ? z4.parse : z4.parseAsync;
        const data: any = await parseFn(schema, values, schemaOptions);

        options.shouldUseNativeValidation && validateFieldsNatively({}, options);

        return {
          errors: {} as any,
          values: resolverOptions.raw ? Object.assign({}, values) : data,
        } as ResolverSuccess<Output | Input>;
      } catch (error) {
        if (isZod4Error(error)) {
          if (process.env.NODE_ENV !== "production") {
            // biome-ignore lint/suspicious/noConsole: temporary form debugging requested by user
            console.log("[Form/ZodResolver] issues", {
              values,
              issues: error.issues.map((issue) => ({
                path: issue.path.join("."),
                message: issue.message,
                code: issue.code,
                input: "input" in issue ? issue.input : undefined,
              })),
            });
          }
          return {
            values: {},
            errors: toNestErrors(
              parseZod4Issues(
                error.issues,
                !options.shouldUseNativeValidation && options.criteriaMode === "all",
                resolverOptions.translateMessage ?? ((message) => message),
              ),
              options,
            ),
          } as ResolverError<Input>;
        }

        throw error;
      }
    };
  }

  throw new Error("Invalid input: not a Zod schema");
}
